import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import { useAnalyticsConsent } from '../../contexts/AnalyticsContext';

/**
 * Cookie Consent Banner
 *
 * Displays a consent banner for analytics tracking (required by Chilean law).
 * Appears at the bottom of the screen until the user makes a decision.
 * The "Rechazar" option requires an extra confirmation step to reduce friction.
 */
export const CookieConsent: React.FC = () => {
  const { giveConsent, isConsentDecided } = useAnalyticsConsent();
  const [rejectModalOpen, setRejectModalOpen] = useState(false);

  if (isConsentDecided) {
    return null;
  }

  const handleRejectConfirm = () => {
    localStorage.setItem('analytics_consent', 'false');
    window.location.reload();
  };

  return (
    <>
      <Paper
        elevation={3}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          p: 2,
          backgroundColor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            maxWidth: 'lg',
            mx: 'auto',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 2,
          }}
        >
          <Typography variant="body2" sx={{ flex: 1 }}>
            Usamos cookies para analizar el uso de la plataforma y mejorar tu experiencia.
            Los datos se recopilan de forma anónima.{' '}
            <Link href="/privacy-policy" underline="hover">
              Política de Privacidad
            </Link>
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
            <Link
              component="button"
              variant="body2"
              underline="hover"
              onClick={() => setRejectModalOpen(true)}
              sx={{ color: 'text.secondary' }}
            >
              No, gracias
            </Link>
            <Button
              variant="contained"
              size="small"
              onClick={giveConsent}
            >
              Aceptar
            </Button>
          </Box>
        </Box>
      </Paper>

      <Dialog
        open={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>¿Seguro que quieres rechazar?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            Las cookies que usamos <strong>no recopilan ningún dato personal</strong>.
            Solo registramos cómo se navega dentro de la plataforma, de forma completamente anónima.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1.5, mb: 1 }}>
            Específicamente, esto nos permite saber:
          </Typography>
          <List dense disablePadding sx={{ pl: 1 }}>
            {[
              'Qué secciones de la plataforma se usan más',
              'En qué pantallas los usuarios encuentran dificultades',
              'Qué funciones son las más valoradas',
            ].map((item) => (
              <ListItem key={item} sx={{ py: 0.25 }}>
                <ListItemText
                  primary={`• ${item}`}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            ))}
          </List>
          <Typography variant="body2" sx={{ mt: 1.5, color: 'text.secondary' }}>
            Esta información nos ayuda a mejorar la experiencia para todos los usuarios.
            Nunca compartimos estos datos con terceros.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="text"
            size="small"
            onClick={handleRejectConfirm}
            sx={{ color: 'text.secondary' }}
          >
            Rechazar de todas formas
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={() => { giveConsent(); setRejectModalOpen(false); }}
          >
            Aceptar cookies
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
